import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useTaskFit } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import type {
  ModelProvider,
  TaskFitDimension,
  TaskFitParams,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { parseAllocationType, resolveEffectiveAllocationType } from '../model-comparison/model-comparison.helpers';
import { MODEL_PROVIDER_VALUES } from '../model-comparison/model-comparison.type';
import { readFiltersFromSearch, resolveStartEnd } from '../sessions-list/filters/session-filters.helpers';
import { useShowAgentMinerApiCost } from '../shared/agent-miner.hooks';
import {
  DEFAULT_TASK_FIT_DIMENSION,
  DEFAULT_TASK_FIT_METRIC,
  TASK_FIT_DIMENSION_VALUES,
  TASK_FIT_METRIC_VALUES,
} from './task-fit.type';
import type { TaskFitMetric } from './task-fit.type';

const DEFAULT_WINDOW_DAYS = 30;

/**
 * URL-synced row dimension for the Task Fit matrix, held in the
 * `taskfit_dimension` param so the choice survives navigation and is shareable
 * like the rest of the page's filter state. The default (`raw_type`) omits the
 * param entirely so shared URLs stay clean, and an unrecognized hand-edited
 * value falls back to the default rather than sending garbage to the backend.
 *
 * @returns {{ dimension: TaskFitDimension, setDimension: (next: TaskFitDimension) => void }}
 */
const useTaskFitDimension = (): {
  dimension: TaskFitDimension;
  setDimension: (next: TaskFitDimension) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('taskfit_dimension');
  const dimension: TaskFitDimension =
    raw && TASK_FIT_DIMENSION_VALUES.has(raw) ? (raw as TaskFitDimension) : DEFAULT_TASK_FIT_DIMENSION;

  const setDimension = (next: TaskFitDimension) => {
    const copy = new URLSearchParams(searchParams);
    if (next === DEFAULT_TASK_FIT_DIMENSION) copy.delete('taskfit_dimension');
    else copy.set('taskfit_dimension', next);
    // Row keys are dimension-scoped, so an open drill-down cannot survive the
    // switch — clear the cell pair to avoid requesting a foreign row key.
    copy.delete('taskfit_cell_row');
    copy.delete('taskfit_cell_family');
    setSearchParams(copy, { replace: true });
  };

  return { dimension, setDimension };
};

/**
 * Resolves the task fit query params from the global organization, the tab's URL
 * date window (the same `started`/`start_date`/`end_date` params the other tabs
 * use, so switching tabs preserves the range), and the active row dimension.
 * Defaults to the trailing 30 days when the URL carries no usable window — this
 * tab is window-scoped, so a carried-over "All time" preset falls back here too.
 *
 * The effective `time_allocation_type` is always included so it is part of the
 * query key and matches what the backend would auto-derive, mirroring
 * {@link useModelComparisonParams}.
 *
 * @returns {TaskFitParams} Query params for useTaskFit
 */
const useTaskFitParams = (): TaskFitParams => {
  const [searchParams] = useSearchParams();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const allocationOverride = searchParams.get('allocation');
  const { dimension } = useTaskFitDimension();

  return useMemo(() => {
    const { start, end } = resolveStartEnd(readFiltersFromSearch(searchParams));
    const endDate = end ?? formatToISODate(new Date());
    // end_date is inclusive on the backend, so a true 30-day window starts 29
    // days back — matching the corrected "Last 30 days" preset in resolveStartEnd.
    const startDate = start ?? formatToISODate(dayjs(endDate).subtract(DEFAULT_WINDOW_DAYS - 1, 'day'));
    return {
      organization_id: organizationId ?? '',
      start_date: startDate,
      end_date: endDate,
      dimension,
      time_allocation_type: resolveEffectiveAllocationType(parseAllocationType(allocationOverride), startDate, endDate),
    };
  }, [searchParams, organizationId, allocationOverride, dimension]);
};

/**
 * URL-synced provider filter for the Task Fit matrix, mirroring
 * {@link useModelComparisonProviderFilter} but on a Task-Fit-specific
 * `taskfit_model_provider` param so the two tabs don't share a selection.
 * Reads/writes a comma-separated list validated against known
 * {@link ModelProvider} values (deduplicated and lowercased first, so a
 * repeated or differently-cased hand-edited token can't produce duplicate
 * pills or get dropped). Filtering happens entirely client-side over the
 * already-fetched matrix columns, so this doesn't affect the query key — an
 * empty selection means "no filter".
 *
 * @returns {{ providers: ModelProvider[], setProviders: (next: ModelProvider[]) => void }}
 */
const useTaskFitProviderFilter = (): {
  providers: ModelProvider[];
  setProviders: (next: ModelProvider[]) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('taskfit_model_provider');

  const providers = useMemo(() => {
    const tokens = raw ? raw.split(',').filter(Boolean) : [];
    const deduped = Array.from(new Set(tokens.map((token) => token.toLowerCase())));
    return deduped.filter((value): value is ModelProvider => MODEL_PROVIDER_VALUES.has(value));
  }, [raw]);

  const setProviders = (next: ModelProvider[]) => {
    const copy = new URLSearchParams(searchParams);
    if (next.length === 0) copy.delete('taskfit_model_provider');
    else copy.set('taskfit_model_provider', next.join(','));
    setSearchParams(copy, { replace: true });
  };

  return { providers, setProviders };
};

/**
 * URL-synced set of model families hidden from the Task Fit matrix, held in a
 * comma-separated `taskfit_hidden_families` param so the matrix and the filter
 * panel's per-model toggles share one source of truth (they render in
 * different PageLayout slots and can't share component state). Mirrors
 * {@link useModelComparisonHiddenFamilies}; values are free-form display labels
 * because the backend passes unmatched model ids through verbatim.
 *
 * @returns {{ hiddenFamilies: Set<string>, toggleFamily: (family: string) => void, setHiddenFamilies: (next: Set<string>) => void }}
 */
const useTaskFitHiddenFamilies = (): {
  hiddenFamilies: Set<string>;
  toggleFamily: (family: string) => void;
  setHiddenFamilies: (next: Set<string>) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('taskfit_hidden_families');

  const hiddenFamilies = useMemo(() => new Set((raw ? raw.split(',') : []).filter(Boolean)), [raw]);

  const setHiddenFamilies = (next: Set<string>) => {
    const copy = new URLSearchParams(searchParams);
    if (next.size === 0) copy.delete('taskfit_hidden_families');
    else copy.set('taskfit_hidden_families', Array.from(next).join(','));
    setSearchParams(copy, { replace: true });
  };

  const toggleFamily = (family: string) => {
    const next = new Set(hiddenFamilies);
    if (next.has(family)) next.delete(family);
    else next.add(family);
    setHiddenFamilies(next);
  };

  return { hiddenFamilies, toggleFamily, setHiddenFamilies };
};

/**
 * URL-synced toggle for whether the unattributable "Unknown" model row is shown
 * in the Task Fit matrix, held in the `taskfit_show_unknown` param. Unknown is
 * HIDDEN by default, so the ABSENCE of the param means hidden and a clean/shared
 * URL hides it; only `taskfit_show_unknown=1` reveals it. Any other value is
 * treated as "not 1" (hidden), so a hand-edited URL can't inject a third state.
 *
 * @returns {{ showUnknown: boolean, setShowUnknown: (next: boolean) => void }}
 */
const useTaskFitShowUnknown = (): {
  showUnknown: boolean;
  setShowUnknown: (next: boolean) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showUnknown = searchParams.get('taskfit_show_unknown') === '1';

  const setShowUnknown = (next: boolean) => {
    const copy = new URLSearchParams(searchParams);
    if (next) copy.set('taskfit_show_unknown', '1');
    else copy.delete('taskfit_show_unknown');
    setSearchParams(copy, { replace: true });
  };

  return { showUnknown, setShowUnknown };
};

/**
 * URL-synced heatmap metric for the Task Fit matrix, held in the
 * `taskfit_metric` param so the choice survives navigation like the dimension
 * toggle. The default (`rework`) omits the param so shared URLs stay clean, and
 * an unrecognized hand-edited value falls back to the default.
 *
 * @returns {{ metric: TaskFitMetric, setMetric: (next: TaskFitMetric) => void }}
 */
const useTaskFitMetric = (): {
  metric: TaskFitMetric;
  setMetric: (next: TaskFitMetric) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('taskfit_metric');
  const metric: TaskFitMetric =
    raw && TASK_FIT_METRIC_VALUES.has(raw) ? (raw as TaskFitMetric) : DEFAULT_TASK_FIT_METRIC;

  const setMetric = (next: TaskFitMetric) => {
    const copy = new URLSearchParams(searchParams);
    if (next === DEFAULT_TASK_FIT_METRIC) copy.delete('taskfit_metric');
    else copy.set('taskfit_metric', next);
    setSearchParams(copy, { replace: true });
  };

  return { metric, setMetric };
};

/**
 * The single source of truth for "which metric is actually shown", composing
 * {@link useTaskFitMetric} with the cost-visibility gate: the frontend flag
 * (`financials_read` + the org's `hide_agent_miner_api_cost` override, via
 * {@link useShowAgentMinerApiCost}) ANDed with the backend's `meta.cost_visible`.
 * `TaskFitFilters` (the metric Select) and `TaskFitMain` (the matrix) both
 * consume this instead of each re-deriving `effectiveMetric` from `metric` +
 * `costAllowed` independently — a stale `cost` selection degrades to `rework`
 * for a viewer without cost visibility identically in both places, by
 * construction rather than by two call sites happening to agree.
 *
 * @returns {{ metric: TaskFitMetric, effectiveMetric: TaskFitMetric, costAllowed: boolean, setMetric: (next: TaskFitMetric) => void }}
 */
const useTaskFitEffectiveMetric = (): {
  metric: TaskFitMetric;
  effectiveMetric: TaskFitMetric;
  costAllowed: boolean;
  setMetric: (next: TaskFitMetric) => void;
} => {
  const { metric, setMetric } = useTaskFitMetric();
  const showApiCost = useShowAgentMinerApiCost();
  const params = useTaskFitParams();
  const { data } = useTaskFit(params);
  const costAllowed = (data?.meta.cost_visible ?? false) && showApiCost;
  const effectiveMetric: TaskFitMetric = metric === 'cost' && !costAllowed ? 'rework' : metric;

  return { metric, effectiveMetric, costAllowed, setMetric };
};

/** The matrix cell whose tasks the side panel lists (a row/family address). */
type TaskFitSelectedCell = { rowKey: string; modelFamily: string };

/**
 * URL-synced address of the matrix cell whose tasks are open in the side
 * panel, held in the `taskfit_cell_row`/`taskfit_cell_family` param pair so the
 * drill-down survives refresh and is shareable. Both params must be present for
 * a cell to count as selected — a lone stale half is treated as closed. Setting
 * null closes the panel by dropping both params.
 *
 * @returns {{ cell: TaskFitSelectedCell | null, setCell: (next: TaskFitSelectedCell | null) => void }}
 */
const useTaskFitSelectedCell = (): {
  cell: TaskFitSelectedCell | null;
  setCell: (next: TaskFitSelectedCell | null) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rowKey = searchParams.get('taskfit_cell_row');
  const modelFamily = searchParams.get('taskfit_cell_family');
  const cell = rowKey && modelFamily ? { rowKey, modelFamily } : null;

  const setCell = (next: TaskFitSelectedCell | null) => {
    const copy = new URLSearchParams(searchParams);
    if (next === null) {
      copy.delete('taskfit_cell_row');
      copy.delete('taskfit_cell_family');
    } else {
      copy.set('taskfit_cell_row', next.rowKey);
      copy.set('taskfit_cell_family', next.modelFamily);
    }
    setSearchParams(copy, { replace: true });
  };

  return { cell, setCell };
};

export {
  useTaskFitDimension,
  useTaskFitEffectiveMetric,
  useTaskFitHiddenFamilies,
  useTaskFitMetric,
  useTaskFitParams,
  useTaskFitProviderFilter,
  useTaskFitSelectedCell,
  useTaskFitShowUnknown,
};
export type { TaskFitSelectedCell };
